import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import {
  ChevronDown,
  ChevronRight,
  Sparkles,
  DollarSign,
  Layers,
  CheckSquare,
  Square,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  RotateCcw,
  User,
  Play,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
  GripVertical,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { projectService } from '../../../services/projectService';
import {
  sequencePhases,
  normalizeAiPhaseOrder,
  calcDurationDays,
  formatDuration,
  toISODate,
  STANDARD_PHASES,
} from '../../../utils/phaseSequencing';

const statusColors = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  ongoing: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
};

const getPhaseDisplayStatus = (phase) => phase?.status || 'pending';

const hasExtractedQuotationInfo = (phase) => {
  if (!Array.isArray(phase?.details_json)) return false;
  return phase?.details_json?.some((item) => String(item || '')?.trim()?.length > 0);
};

const storeyLabel = (storey) => {
  if (!storey) return null;
  const s = String(storey)?.toLowerCase();
  if (s?.includes('first') || s === '1' || s === '1st') return '1st Storey';
  if (s?.includes('second') || s === '2' || s === '2nd') return '2nd Storey';
  return storey;
};

const formatCurrency = (value) => {
  if (value == null || value === '') return null;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return null;

  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 2,
  })?.format(num);
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';

  try {
    return new Date(dateStr)?.toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const normalizeWhitespace = (text) => String(text || '')?.replace(/\s+/g, ' ')?.trim();

const isTaskLine = (text) => {
  if (typeof text !== 'string') return false;
  return /^\d+[a-zA-Z]?\s/?.test(text?.trim());
};

const isStoreyText = (text) => {
  const normalized = normalizeWhitespace(text)?.toLowerCase();
  return /storey|floor|level/i?.test(normalized);
};

const isIgnorableLabel = (text) => {
  const normalized = normalizeWhitespace(text)?.toLowerCase();
  if (!normalized) return true;

  const ignoredPatterns = [
    /^supply labour[, ]+material and equipment to$/,
    /^supply labour, material and equipment to$/,
    /^labour, material and equipment$/,
    /^material and equipment$/,
    /^to$/,
    /^\(prices are subjected to change at final design & material stage\)$/,
    /^prices are subjected to change at final design & material stage$/,
    /^to supply materials, fabricate & install the following items as per proposed drawing-?$/,
  ];

  return ignoredPatterns?.some((pattern) => pattern?.test(normalized));
};

const buildContextualTaskText = ({ text, parentLabel }) => {
  const cleanTask = normalizeWhitespace(text);
  const cleanParentLabel = normalizeWhitespace(parentLabel);

  if (
    cleanParentLabel &&
    !isIgnorableLabel(cleanParentLabel) &&
    !isStoreyText(cleanParentLabel)
  ) {
    return `${cleanTask} — ${cleanParentLabel}`;
  }

  return cleanTask;
};

const transformDetailItems = (rawItems) => {
  return (rawItems || [])
    ?.map((item) => {
      const text =
        typeof item === 'string'
          ? item
          : item?.description || item?.item || item?.name || JSON.stringify(item);

      const normalizedText = normalizeWhitespace(text);
      if (!normalizedText) return null;

      return {
        type: isTaskLine(normalizedText) ? 'task' : 'label',
        text: normalizedText,
      };
    })
    ?.filter(Boolean);
};

const parseDetailsJson = (detailsJson) => {
  if (!detailsJson) return [];

  try {
    const parsed = typeof detailsJson === 'string' ? JSON.parse(detailsJson) : detailsJson;
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return Object.values(parsed);
    return [];
  } catch {
    return [];
  }
};

const getDefaultDuration = (phaseName) => {
  const name = (phaseName || '')?.toLowerCase();

  const standard = STANDARD_PHASES?.find(
    (sp) =>
      sp?.name?.toLowerCase() === name ||
      name?.includes(sp?.name?.toLowerCase()) ||
      sp?.name?.toLowerCase()?.includes(name)
  );

  if (standard?.durationDays) return standard?.durationDays;

  const seed = (phaseName || 'phase')?.length % 10;
  return 5 + seed;
};

const PhaseCard = ({
  phase,
  phaseTasks,
  onToggle,
  isDraft,
  phaseOrder,
  workers = [],
  onAssignWorkerForPhase,
  assigningPhaseId,
  isWorkerView = false,
  isOngoing = false,
  onNextPhase,
  advancingPhase = false,
  isLastPhase = false,
  onCompleteProject,
  onPhaseOwnerChoice,
  selectingOwnerPhaseId,
  onResetPhaseOwner,
  resettingOwnerPhaseId,
  onAddTask,
  onRenameTask,
  onDeleteTask,
  onMoveTask,
  taskEditBusyId,
  onRenamePhase,
  onDeletePhase,
  phaseEditBusyId,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskText, setEditingTaskText] = useState('');
  const [editingPhaseName, setEditingPhaseName] = useState(false);
  const [phaseNameText, setPhaseNameText] = useState(
    phase?.phase_name || phase?.source_task_name || ''
  );

  useEffect(() => {
    if (!editingPhaseName) {
      setPhaseNameText(phase?.phase_name || phase?.source_task_name || '');
    }
  }, [phase?.phase_name, phase?.source_task_name, editingPhaseName]);

  const structuredItems = useMemo(() => {
    if ((phase?.owner_type || 'unselected') === 'unselected') return [];

    return (phaseTasks || [])?.map((task) => ({
      type: 'task',
      persistedTask: task,
      displayText: task?.task_text,
      text: task?.task_text,
    }));
  }, [phase?.owner_type, phaseTasks]);

  const taskItems = useMemo(
    () => structuredItems?.filter((item) => item?.type === 'task'),
    [structuredItems]
  );

  const totalItems = taskItems?.length || 0;
  const completedItems = taskItems?.filter((item) => !!item?.persistedTask?.is_completed)?.length || 0;

  const taskBasedProgress =
    totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : (phase?.progress || 0);

  const progress =
    phase?.status === 'completed' ? Number(phase?.progress ?? 100) || 100 : taskBasedProgress;

  const displayStatus = getPhaseDisplayStatus(phase);
  const isReadyForBossReview = progress >= 100 && displayStatus !== 'completed';
  const estStart = phase?.estimated_start_date || phase?.planned_start_date;
  const estEnd = phase?.estimated_end_date || phase?.planned_end_date;
  const phaseDurationLabel = formatDuration(phase?.durationDays || getDefaultDuration(phase?.phase_name));
  const phaseHasQuoteInfo = hasExtractedQuotationInfo(phase);
  const phaseIsBusy = phaseEditBusyId === phase?.id;

  const phaseAssignedWorkerId = useMemo(() => {
    const firstAssignedTask = (phaseTasks || [])?.find((task) => task?.assigned_worker_id);
    return firstAssignedTask?.assigned_worker_id || '';
  }, [phaseTasks]);

  const handleCheckboxClick = (taskId, currentChecked) => {
    if (!taskId) return;
    onToggle?.(taskId, currentChecked);
  };

  const handleStartEditTask = (task) => {
    if (!task?.id) return;
    setEditingTaskId(task?.id);
    setEditingTaskText(task?.task_text || '');
  };

  const handleCancelEditTask = () => {
    setEditingTaskId(null);
    setEditingTaskText('');
  };

  const handleSaveEditTask = async (task) => {
    const nextText = editingTaskText?.trim();
    if (!task?.id || !nextText) return;

    await onRenameTask?.(phase?.id, task?.id, nextText);
    setEditingTaskId(null);
    setEditingTaskText('');
  };

  const handleAddTask = async () => {
    const text = newTaskText?.trim();
    if (!text || !phase?.id) return;

    await onAddTask?.(phase?.id, text);
    setNewTaskText('');
  };

  const handleSavePhaseName = async () => {
    const nextName = phaseNameText?.trim();
    if (!phase?.id || !nextName) return;

    await onRenamePhase?.(phase?.id, nextName);
    setEditingPhaseName(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div
        className="flex items-start justify-between p-5 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {phaseOrder != null && (
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                {phaseOrder}
              </span>
            )}

            <div className="flex items-center gap-1 min-w-0" onClick={(e) => e?.stopPropagation()}>
              {editingPhaseName ? (
                <div className="flex items-center gap-1 min-w-[220px]">
                  <input
                    value={phaseNameText}
                    onChange={(e) => setPhaseNameText(e?.target?.value)}
                    onKeyDown={(e) => {
                      if (e?.key === 'Enter') handleSavePhaseName();
                      if (e?.key === 'Escape') setEditingPhaseName(false);
                    }}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleSavePhaseName}
                    disabled={phaseIsBusy || !phaseNameText?.trim()}
                    className="p-1.5 rounded-md text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Save phase name"
                  >
                    <Save size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingPhaseName(false)}
                    disabled={phaseIsBusy}
                    className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Cancel phase rename"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-base font-semibold text-foreground truncate">
                    {phase?.phase_name || phase?.source_task_name}
                  </h3>

                  {phaseHasQuoteInfo && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-semibold"
                      title="This phase has information extracted from the quotation"
                    >
                      Quote
                    </span>
                  )}

                  {!isWorkerView && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setPhaseNameText(phase?.phase_name || phase?.source_task_name || '');
                          setEditingPhaseName(true);
                        }}
                        disabled={phaseIsBusy}
                        className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Rename phase"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeletePhase?.(phase)}
                        disabled={phaseIsBusy}
                        className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Delete phase"
                      >
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                statusColors?.[displayStatus] || statusColors?.pending
              }`}
            >
              {displayStatus}
            </span>

            {isReadyForBossReview && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium bg-amber-100 text-amber-700 border-amber-200">
                Ready for Boss Review
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
            {storeyLabel(phase?.storey) && (
              <span className="flex items-center gap-1">
                <Layers size={13} />
                {storeyLabel(phase?.storey)}
              </span>
            )}

            <span className="flex items-center gap-1 text-primary/80 font-medium">
              <Clock size={13} />
              Est. {phaseDurationLabel}
            </span>

            {estStart && estEnd ? (
              <span className="flex items-center gap-1 text-blue-600 font-medium">
                <Calendar size={13} />
                {formatDisplayDate(estStart)} — {formatDisplayDate(estEnd)}
              </span>
            ) : !isDraft && !isWorkerView && formatCurrency(phase?.total_cost) ? (
              <span className="flex items-center gap-1 text-emerald-700 font-medium">
                <DollarSign size={13} />
                {formatCurrency(phase?.total_cost)}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground/60 italic text-xs">
                <Calendar size={13} />
                Set start date to see schedule
              </span>
            )}

            {totalItems > 0 && (
              <span className="flex items-center gap-1">
                <CheckSquare size={13} />
                {completedItems}/{totalItems} items
              </span>
            )}
          </div>

          {!isWorkerView && (phase?.owner_type || 'unselected') === 'unselected' && (
            <div
              className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50"
              onClick={(e) => e?.stopPropagation()}
            >
              <p className="text-sm font-medium text-foreground mb-2">
                Is this phase done by your own men?
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={selectingOwnerPhaseId === phase?.id}
                  onClick={() => onPhaseOwnerChoice?.(phase, 'internal')}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-green-200 bg-white text-green-700 text-sm font-medium hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckSquare size={15} />
                  Yes
                </button>

                <button
                  type="button"
                  disabled={selectingOwnerPhaseId === phase?.id}
                  onClick={() => onPhaseOwnerChoice?.(phase, 'external')}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Square size={15} />
                  No
                </button>
              </div>

              {selectingOwnerPhaseId === phase?.id && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Creating task checklist...
                </p>
              )}
            </div>
          )}

          {!isWorkerView && (phase?.owner_type || 'unselected') !== 'unselected' && (
            <div
              className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"
              onClick={(e) => e?.stopPropagation()}
            >
              <span>
                Phase type:{' '}
                <span className="font-medium text-foreground">
                  {phase?.owner_type === 'internal' ? 'Done by us' : 'Outsourced'}
                </span>
              </span>

              <button
                type="button"
                disabled={resettingOwnerPhaseId === phase?.id}
                onClick={() => onResetPhaseOwner?.(phase)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw size={12} />
                {resettingOwnerPhaseId === phase?.id ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          )}

          {!isWorkerView && (phase?.owner_type || 'unselected') !== 'unselected' && (
            <div className="mt-3 max-w-xs" onClick={(e) => e?.stopPropagation()}>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Assign Worker for Entire Phase
              </label>

              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={phaseAssignedWorkerId}
                  onChange={(e) => onAssignWorkerForPhase?.(phase?.id, e?.target?.value || null)}
                  className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                >
                  <option value="">Unassigned</option>
                  {workers?.map((worker) => (
                    <option key={worker?.id} value={worker?.id}>
                      {worker?.full_name}{worker?.trade ? ` — ${worker?.trade}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {assigningPhaseId === phase?.id && (
                <p className="mt-1 text-xs text-muted-foreground">Saving phase assignment...</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          <div className="flex flex-col items-end gap-1">
            <span className="text-sm font-bold text-primary">{progress}%</span>
            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {!isWorkerView && isOngoing && onNextPhase && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                if (isLastPhase && onCompleteProject) {
                  onCompleteProject(phase?.id);
                } else {
                  onNextPhase(phase?.id);
                }
              }}
              disabled={advancingPhase}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isLastPhase ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {advancingPhase ? (
                <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : isLastPhase ? (
                <CheckCircle2 size={13} />
              ) : (
                <ArrowRight size={13} />
              )}
              {isLastPhase ? 'Complete Project' : 'Next Phase'}
            </button>
          )}

          {collapsed ? (
            <ChevronRight size={18} className="text-muted-foreground" />
          ) : (
            <ChevronDown size={18} className="text-muted-foreground" />
          )}
        </div>
      </div>

      {!collapsed && structuredItems?.length > 0 && (
        <div className="border-t border-border px-5 py-4 space-y-1">
          {structuredItems?.map((item, i) => {
            const taskId = item?.persistedTask?.id;
            const checked = !!item?.persistedTask?.is_completed;
            const isEditing = editingTaskId === taskId;
            const isBusy =
              taskEditBusyId === taskId ||
              taskEditBusyId === `${phase?.id}:add` ||
              taskEditBusyId === `${phase?.id}:reorder`;

            return (
              <div key={`${phase?.id}-task-${taskId || i}`} className="py-1">
                <div className="flex items-start gap-3 group">
                  <button
                    type="button"
                    className={`mt-0.5 flex-shrink-0 ${taskId ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      if (taskId) handleCheckboxClick(taskId, checked);
                    }}
                    disabled={!taskId}
                    title={checked ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {checked ? (
                      <CheckSquare size={18} className="text-primary" />
                    ) : (
                      <Square size={18} className="text-muted-foreground group-hover:text-primary" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0" onClick={(e) => e?.stopPropagation()}>
                    {isEditing ? (
                      <input
                        value={editingTaskText}
                        onChange={(e) => setEditingTaskText(e?.target?.value)}
                        onKeyDown={(e) => {
                          if (e?.key === 'Enter') handleSaveEditTask(item?.persistedTask);
                          if (e?.key === 'Escape') handleCancelEditTask();
                        }}
                        className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        autoFocus
                      />
                    ) : (
                      <span
                        className={`text-sm leading-relaxed block ${
                          checked ? 'line-through text-muted-foreground' : 'text-foreground'
                        }`}
                      >
                        {item?.displayText || item?.text}
                      </span>
                    )}
                  </div>

                  {!isWorkerView && taskId && (
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e?.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveEditTask(item?.persistedTask)}
                            disabled={isBusy || !editingTaskText?.trim()}
                            className="p-1.5 rounded-md text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditTask}
                            disabled={isBusy}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => onMoveTask?.(phase?.id, taskId, -1)}
                            disabled={isBusy || i === 0}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowUp size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onMoveTask?.(phase?.id, taskId, 1)}
                            disabled={isBusy || i === taskItems?.length - 1}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowDown size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStartEditTask(item?.persistedTask)}
                            disabled={isBusy}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteTask?.(phase?.id, taskId)}
                            disabled={isBusy}
                            className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {!isWorkerView && (phase?.owner_type || 'unselected') !== 'unselected' && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-2" onClick={(e) => e?.stopPropagation()}>
              <input
                value={newTaskText}
                onChange={(e) => setNewTaskText(e?.target?.value)}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter') handleAddTask();
                }}
                placeholder="Add a new task..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <button
                type="button"
                onClick={handleAddTask}
                disabled={!newTaskText?.trim() || taskEditBusyId === `${phase?.id}:add`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={14} />
                Add Task
              </button>
            </div>
          )}
        </div>
      )}

      {!collapsed && structuredItems?.length === 0 && !isWorkerView && (phase?.owner_type || 'unselected') !== 'unselected' && (
        <div className="border-t border-border px-5 py-4" onClick={(e) => e?.stopPropagation()}>
          <div className="flex items-center gap-2">
            <input
              value={newTaskText}
              onChange={(e) => setNewTaskText(e?.target?.value)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter') handleAddTask();
              }}
              placeholder="Add a new task..."
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <button
              type="button"
              onClick={handleAddTask}
              disabled={!newTaskText?.trim() || taskEditBusyId === `${phase?.id}:add`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus size={14} />
              Add Task
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PhaseSequencingOverride = ({
  phases,
  onReorder,
  onManualDateChange,
  savingPhaseDateId,
  onAddPhase,
  addingCustomPhase = false,
}) => {
  const [localOrder, setLocalOrder] = useState(() =>
    phases?.map((p, i) => ({ ...p, _localOrder: i }))
  );
  const [newPhaseName, setNewPhaseName] = useState('');
  const [draggingPhaseId, setDraggingPhaseId] = useState(null);

  useEffect(() => {
    setLocalOrder(phases?.map((p, i) => ({ ...p, _localOrder: i })));
  }, [phases]);

  const sequenced = useMemo(() => localOrder || [], [localOrder]);

  const reorderToIndex = (fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const next = [...localOrder];
    const [moved] = next?.splice(fromIndex, 1);
    next?.splice(toIndex, 0, moved);

    const updated = next?.map((p, i) => ({ ...p, phase_order: i + 1 }));
    setLocalOrder(updated);
    onReorder?.(updated);
  };

  const resetOrder = () => {
    const normalized = normalizeAiPhaseOrder(phases);
    setLocalOrder(normalized);
    onReorder?.(normalized);
  };

  const handleAddCustomPhase = async () => {
    const cleanName = newPhaseName?.trim();
    if (!cleanName || addingCustomPhase) return;

    const addedPhase = await onAddPhase?.(cleanName);

    if (addedPhase) {
      setNewPhaseName('');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">
          Drag to reorder phases. Start and end dates are inline for quicker editing.
        </p>

        <button
          onClick={resetOrder}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <RotateCcw size={12} />
          Reset order
        </button>
      </div>
      <div className="mb-3 rounded-lg border border-border bg-background p-3">
        <label className="block text-xs font-medium text-foreground mb-1">
          Add Custom Phase
        </label>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={newPhaseName}
            onChange={(e) => setNewPhaseName(e?.target?.value)}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') handleAddCustomPhase();
            }}
            placeholder="e.g. Site inspection, Custom carpentry, Final handover"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />

          <button
            type="button"
            onClick={handleAddCustomPhase}
            disabled={!newPhaseName?.trim() || addingCustomPhase}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {addingCustomPhase ? (
              <div className="w-4 h-4 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            Add Phase
          </button>
        </div>
      </div>
      {sequenced?.map((phase, index) => {
        const duration = phase?.durationDays || getDefaultDuration(phase?.phase_name);
        const durationLabel = formatDuration(duration);
        const estStart = phase?.estimated_start_date || phase?.planned_start_date || '';
        const estEnd = phase?.estimated_end_date || phase?.planned_end_date || '';
        const isDragging = draggingPhaseId === phase?.id;

        return (
          <div
            key={phase?.id}
            draggable
            onDragStart={() => setDraggingPhaseId(phase?.id)}
            onDragEnd={() => setDraggingPhaseId(null)}
            onDragOver={(e) => e?.preventDefault()}
            onDrop={() => {
              const fromIndex = sequenced?.findIndex((p) => p?.id === draggingPhaseId);
              reorderToIndex(fromIndex, index);
              setDraggingPhaseId(null);
            }}
            className={`p-3 rounded-lg border transition-colors ${
              isDragging
                ? 'border-primary bg-primary/5' :'bg-muted/30 border-border hover:bg-muted/50'
            }`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_160px_160px] gap-3 items-center">
              <div className="flex items-center gap-3 min-w-0">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                  {index + 1}
                </span>

                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted text-muted-foreground cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                >
                  <GripVertical size={14} />
                </button>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {phase?.phase_name || phase?.required_trade}
                  </p>
                  <p className="text-xs text-primary/80 mt-0.5">{durationLabel}</p>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                  Start
                </label>
                <input
                  type="date"
                  value={estStart}
                  onChange={(e) => onManualDateChange?.(phase?.id, 'planned_start_date', e?.target?.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                  End
                </label>
                <input
                  type="date"
                  value={estEnd}
                  onChange={(e) => onManualDateChange?.(phase?.id, 'planned_end_date', e?.target?.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>
            {savingPhaseDateId === phase?.id && (
              <p className="mt-2 text-xs text-muted-foreground">Saving phase dates...</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AIPhaseManagement = ({
  project,
  phases: initialPhases,
  projectName,
  onBack,
  userRole = 'boss',
  currentUserId = null,
  onProjectStart = null,
}) => {
  const navigate = useNavigate();

  const [phaseTasksMap, setPhaseTasksMap] = useState({});
  const [workers, setWorkers] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [assigningPhaseId, setAssigningPhaseId] = useState(null);
  const [savingPhaseDateId, setSavingPhaseDateId] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [startingProject, setStartingProject] = useState(false);
  const [startError, setStartError] = useState('');
  const [advancingPhaseId, setAdvancingPhaseId] = useState(null);
  const [selectingOwnerPhaseId, setSelectingOwnerPhaseId] = useState(null);
  const [resettingOwnerPhaseId, setResettingOwnerPhaseId] = useState(null);
  const [taskEditBusyId, setTaskEditBusyId] = useState(null);
  const [phaseEditBusyId, setPhaseEditBusyId] = useState(null);
  const [addingCustomPhase, setAddingCustomPhase] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameText, setProjectNameText] = useState(projectName || project?.name || '');
  const [displayProjectName, setDisplayProjectName] = useState(projectName || project?.name || '');
  const [savingProjectName, setSavingProjectName] = useState(false);

  const [phases, setPhases] = useState(initialPhases || []);
  const [projectStartDate, setProjectStartDate] = useState(project?.start_date || '');
  const [sequencing, setSequencing] = useState(false);
  const [sequencingDone, setSequencingDone] = useState(false);
  const [sequencingError, setSequencingError] = useState('');
  const [showSequenceOverride, setShowSequenceOverride] = useState(false);
  const [reorderedPhases, setReorderedPhases] = useState(null);

  const isDraft = project?.status === 'draft';
  const hasNoSchedule = !project?.start_date;
  const isWorkerView = userRole === 'worker';
  const [showSequencingPanel, setShowSequencingPanel] = useState(isDraft || hasNoSchedule);

  useEffect(() => {
    setPhases(initialPhases || []);
  }, [initialPhases]);

  useEffect(() => {
    setProjectStartDate(project?.start_date || '');
  }, [project?.start_date]);

  useEffect(() => {
    const nextName = projectName || project?.name || '';
    setDisplayProjectName(nextName);
    setProjectNameText(nextName);
    setEditingProjectName(false);
  }, [project?.id, projectName, project?.name]);

  const refreshProjectPhasesFromSupabase = useCallback(async () => {
    if (!project?.id) return [];

    try {
      const { data, error } = await supabase
        ?.from('phases')
        ?.select('*')
        ?.eq('project_id', project?.id)
        ?.order('phase_order', { ascending: true });

      if (error) {
        console.error('Error refreshing phases from Supabase:', error);
        return [];
      }

      const freshPhases = data || [];
      setPhases(freshPhases);
      setReorderedPhases(null);
      return freshPhases;
    } catch (err) {
      console.error('Unexpected error refreshing phases from Supabase:', err);
      return [];
    }
  }, [project?.id]);

  useEffect(() => {
    if (!project?.id) return;
    refreshProjectPhasesFromSupabase();
  }, [project?.id, refreshProjectPhasesFromSupabase]);

  useEffect(() => {
    if (isWorkerView) return;

    const loadWorkers = async () => {
      const { data, error } = await supabase
        ?.from('users')
        ?.select('id, full_name, trade')
        ?.order('full_name', { ascending: true });

      if (error) {
        console.error('Error loading workers:', error);
        return;
      }

      setWorkers(data || []);
    };

    loadWorkers();
  }, [isWorkerView]);

  const sortedPhases = useMemo(() => {
    const base = reorderedPhases || phases || [];
    return [...base]?.sort((a, b) => (Number(a?.phase_order) || 0) - (Number(b?.phase_order) || 0));
  }, [phases, reorderedPhases]);

  const phaseIdsKey = useMemo(() => {
    return (sortedPhases || [])?.map((phase) => phase?.id)?.filter(Boolean)?.join('|') || '';
  }, [sortedPhases]);

  const estimatedEndDate = useMemo(() => {
    if (!sortedPhases?.length) return project?.end_date || null;

    const withDates = sortedPhases?.filter((p) => p?.estimated_end_date || p?.planned_end_date);
    if (!withDates?.length) return project?.end_date || null;

    return (
      withDates?.[withDates?.length - 1]?.estimated_end_date ||
      withDates?.[withDates?.length - 1]?.planned_end_date ||
      project?.end_date ||
      null
    );
  }, [sortedPhases, project?.end_date]);

  const estimatedDurationDays = useMemo(() => {
    const start = projectStartDate || project?.start_date;
    const end = estimatedEndDate;
    return calcDurationDays(start, end);
  }, [projectStartDate, project?.start_date, estimatedEndDate]);

  const createInternalTasksFromPhaseDetails = useCallback(async (phase) => {
    const rawItems = parseDetailsJson(phase?.details_json);
    const transformed = transformDetailItems(rawItems);

    let lastUsefulLabel = null;
    let taskOrder = 0;
    const rowsToInsert = [];

    transformed?.forEach((item) => {
      if (item?.type === 'label') {
        if (!isIgnorableLabel(item?.text) && !isStoreyText(item?.text)) {
          lastUsefulLabel = item?.text;
        }
        return;
      }

      if (item?.type === 'task') {
        rowsToInsert?.push({
          phase_id: phase?.id,
          task_text: buildContextualTaskText({
            text: item?.text,
            parentLabel: lastUsefulLabel,
          }),
          task_order: taskOrder,
          is_completed: false,
          completed_at: null,
          completed_by: null,
          assigned_worker_id: null,
        });
        taskOrder += 1;
      }
    });

    return rowsToInsert;
  }, []);

  const createExternalTasks = useCallback((phaseId) => [
    {
      phase_id: phaseId,
      task_text: 'Phase 1',
      task_order: 0,
      is_completed: false,
      completed_at: null,
      completed_by: null,
      assigned_worker_id: null,
    },
    {
      phase_id: phaseId,
      task_text: 'Phase 2',
      task_order: 1,
      is_completed: false,
      completed_at: null,
      completed_by: null,
      assigned_worker_id: null,
    },
  ], []);

  const loadAllPhaseTasks = useCallback(async () => {
    try {
      const phaseIds = phaseIdsKey ? phaseIdsKey?.split('|')?.filter(Boolean) : [];
      if (!phaseIds?.length) {
        setPhaseTasksMap({});
        return;
      }

      let query = supabase
        ?.from('phase_tasks')
        ?.select('*')
        ?.in('phase_id', phaseIds)
        ?.order('task_order', { ascending: true });

      if (isWorkerView && currentUserId) {
        query = query?.eq('assigned_worker_id', currentUserId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching phase_tasks:', error);
        return;
      }

      const grouped = {};
      phaseIds?.forEach((id) => {
        grouped[id] = [];
      });

      (data || [])?.forEach((task) => {
        if (grouped?.[task?.phase_id]) {
          grouped?.[task?.phase_id]?.push(task);
        }
      });

      setPhaseTasksMap(grouped);
    } catch (err) {
      console.error('Error loading phase tasks:', err);
      setSeeding(false);
    }
  }, [phaseIdsKey, isWorkerView, currentUserId]);

  useEffect(() => {
    if (!phaseIdsKey) return;
    loadAllPhaseTasks();
  }, [phaseIdsKey, loadAllPhaseTasks]);

  const refetchPhaseTasksForPhase = async (phaseId) => {
    let query = supabase
      ?.from('phase_tasks')
      ?.select('*')
      ?.eq('phase_id', phaseId)
      ?.order('task_order', { ascending: true });

    if (isWorkerView && currentUserId) {
      query = query?.eq('assigned_worker_id', currentUserId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error refetching phase_tasks:', error);
      return null;
    }

    return data || [];
  };

  const updatePhaseProgressFromTasks = useCallback(async (phaseId) => {
    if (!phaseId) return 0;

    const now = new Date()?.toISOString();

    const { data: allTasksForPhase, error } = await supabase
      ?.from('phase_tasks')
      ?.select('id, is_completed')
      ?.eq('phase_id', phaseId);

    if (error) {
      console.error('Error recalculating phase progress:', error);
      return null;
    }

    const total = allTasksForPhase?.length || 0;
    const completed = (allTasksForPhase || [])?.filter((task) => task?.is_completed)?.length;
    const newProgress = total > 0 ? Math.round((completed / total) * 100) : 0;

    const { error: phaseError } = await supabase
      ?.from('phases')
      ?.update({
        progress: newProgress,
        updated_at: now,
      })
      ?.eq('id', phaseId);

    if (phaseError) {
      console.error('Error saving recalculated phase progress:', phaseError);
      return null;
    }

    setPhases((prev) =>
      prev?.map((phase) =>
        phase?.id === phaseId ? { ...phase, progress: newProgress } : phase
      )
    );

    setReorderedPhases((prev) =>
      prev ? prev?.map((phase) => (phase?.id === phaseId ? { ...phase, progress: newProgress } : phase)) : prev
    );

    return newProgress;
  }, []);

  const handlePhaseOwnerChoice = useCallback(async (phase, ownerType) => {
    if (!phase?.id || !ownerType) return;

    setSelectingOwnerPhaseId(phase?.id);
    const now = new Date()?.toISOString();

    const updatePhaseLocally = (updates) => {
      setPhases((prev) =>
        prev?.map((p) => (p?.id === phase?.id ? { ...p, ...updates } : p))
      );

      setReorderedPhases((prev) =>
        prev ? prev?.map((p) => (p?.id === phase?.id ? { ...p, ...updates } : p)) : prev
      );
    };

    const optimisticTasks =
      ownerType === 'external'
        ? createExternalTasks(phase?.id)?.map((task, index) => ({
            ...task,
            id: `temp-${phase?.id}-${index}`,
            task_order: index,
          }))
        : [];

    setPhaseTasksMap((prev) => ({
      ...prev,
      [phase?.id]: optimisticTasks,
    }));

    updatePhaseLocally({
      owner_type: ownerType,
      progress: 0,
      status: 'pending',
      actual_end_date: null,
    });

    try {
      const { error: deleteError } = await supabase
        ?.from('phase_tasks')
        ?.delete()
        ?.eq('phase_id', phase?.id);

      if (deleteError) throw deleteError;

      const rowsToInsert =
        ownerType === 'external'
          ? createExternalTasks(phase?.id)
          : await createInternalTasksFromPhaseDetails(phase);

      let insertedTasks = [];

      if (rowsToInsert?.length > 0) {
        const { data, error: insertError } = await supabase
          ?.from('phase_tasks')
          ?.insert(rowsToInsert)
          ?.select();

        if (insertError) throw insertError;

        insertedTasks = data || [];
      }

      const { error: phaseError } = await supabase
        ?.from('phases')
        ?.update({
          owner_type: ownerType,
          progress: 0,
          status: 'pending',
          actual_end_date: null,
          updated_at: now,
        })
        ?.eq('id', phase?.id);

      if (phaseError) throw phaseError;

      const sortedInsertedTasks = [...insertedTasks]?.sort((a, b) => a?.task_order - b?.task_order);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phase?.id]: sortedInsertedTasks,
      }));

      updatePhaseLocally({
        owner_type: ownerType,
        progress: 0,
        status: 'pending',
        actual_end_date: null,
        updated_at: now,
      });
    } catch (err) {
      console.error('Error selecting phase owner:', err);
      alert(`Failed to create task checklist: ${err?.message || 'Unknown error'}`);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phase?.id]: [],
      }));

      updatePhaseLocally({
        owner_type: 'unselected',
      });
    } finally {
      setSelectingOwnerPhaseId(null);
    }
  }, [createExternalTasks, createInternalTasksFromPhaseDetails]);

  const handleResetPhaseOwner = useCallback(async (phase) => {
    if (!phase?.id) return;

    const confirmed = window.confirm(
      'Reset this phase type? This will remove the current task checklist and let you choose Yes/No again.'
    );
    if (!confirmed) return;

    setResettingOwnerPhaseId(phase?.id);

    try {
      const now = new Date()?.toISOString();

      await supabase?.from('phase_tasks')?.delete()?.eq('phase_id', phase?.id);

      const { error } = await supabase
        ?.from('phases')
        ?.update({
          owner_type: 'unselected',
          progress: 0,
          status: 'pending',
          actual_end_date: null,
          updated_at: now,
        })
        ?.eq('id', phase?.id);

      if (error) throw error;

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phase?.id]: [],
      }));

      setPhases((prev) =>
        prev?.map((p) =>
          p?.id === phase?.id
            ? { ...p, owner_type: 'unselected', progress: 0, status: 'pending', actual_end_date: null }
            : p
        )
      );

      setReorderedPhases((prev) =>
        prev ? prev?.map((p) => (p?.id === phase?.id ? { ...p, owner_type: 'unselected', progress: 0, status: 'pending', actual_end_date: null } : p)) : prev
      );
    } catch (err) {
      console.error('Error resetting phase owner:', err);
    } finally {
      setResettingOwnerPhaseId(null);
    }
  }, []);

  const handleToggle = useCallback(async (taskId, currentChecked) => {
    if (!taskId) return;

    const now = new Date()?.toISOString();

    let phaseId = null;
    let taskBeforeUpdate = null;

    Object.entries(phaseTasksMap || {})?.some(([currentPhaseId, tasks]) => {
      const foundTask = (tasks || [])?.find((task) => task?.id === taskId);
      if (foundTask) {
        phaseId = currentPhaseId;
        taskBeforeUpdate = foundTask;
        return true;
      }
      return false;
    });

    if (!phaseId || !taskBeforeUpdate) return;

    const newChecked = !currentChecked;
    const previousTasksForPhase = phaseTasksMap?.[phaseId] || [];

    const nextTasksForPhase = previousTasksForPhase?.map((task) =>
      task?.id === taskId
        ? { ...task, is_completed: newChecked, completed_at: newChecked ? now : null, updated_at: now }
        : task
    );

    setPhaseTasksMap((prev) => ({
      ...prev,
      [phaseId]: nextTasksForPhase,
    }));

    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();

      const completedBy = newChecked ? user?.id : null;

      const { error } = await supabase
        ?.from('phase_tasks')
        ?.update({
          is_completed: newChecked,
          completed_at: newChecked ? now : null,
          completed_by: completedBy,
          updated_at: now,
        })
        ?.eq('id', taskId);

      if (error) throw error;

      await updatePhaseProgressFromTasks(phaseId);
    } catch (err) {
      console.error('Error toggling task:', err);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: previousTasksForPhase,
      }));

      alert('Failed to save task update. Please try again.');
    }
  }, [phaseTasksMap, updatePhaseProgressFromTasks]);

  const handleAddTask = useCallback(async (phaseId, taskText) => {
    if (!phaseId || !taskText?.trim()) return;

    setTaskEditBusyId(`${phaseId}:add`);

    try {
      const existingTasks = phaseTasksMap?.[phaseId] || [];
      const nextOrder = existingTasks?.length
        ? Math.max(...existingTasks?.map((task) => Number(task?.task_order) || 0)) + 1
        : 0;

      const { error } = await supabase
        ?.from('phase_tasks')
        ?.insert({
          phase_id: phaseId,
          task_text: taskText?.trim(),
          task_order: nextOrder,
          is_completed: false,
          completed_at: null,
          completed_by: null,
          assigned_worker_id: null,
        });

      if (error) throw error;

      const freshTasks = await refetchPhaseTasksForPhase(phaseId);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: freshTasks || [],
      }));

      await updatePhaseProgressFromTasks(phaseId);
    } catch (err) {
      console.error('Error adding task:', err);
    } finally {
      setTaskEditBusyId(null);
    }
  }, [phaseTasksMap, updatePhaseProgressFromTasks]);

  const handleRenameTask = useCallback(async (phaseId, taskId, taskText) => {
    if (!phaseId || !taskId || !taskText?.trim()) return;

    setTaskEditBusyId(taskId);

    try {
      const { error } = await supabase
        ?.from('phase_tasks')
        ?.update({
          task_text: taskText?.trim(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', taskId);

      if (error) throw error;

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: (prev?.[phaseId] || [])?.map((task) =>
          task?.id === taskId ? { ...task, task_text: taskText?.trim() } : task
        ),
      }));
    } catch (err) {
      console.error('Error renaming task:', err);
    } finally {
      setTaskEditBusyId(null);
    }
  }, []);

  const handleDeleteTask = useCallback(async (phaseId, taskId) => {
    if (!phaseId || !taskId) return;

    const confirmed = window.confirm('Remove this task from the phase?');
    if (!confirmed) return;

    setTaskEditBusyId(taskId);

    try {
      const { error } = await supabase?.from('phase_tasks')?.delete()?.eq('id', taskId);
      if (error) throw error;

      const freshTasks = await refetchPhaseTasksForPhase(phaseId);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: freshTasks || [],
      }));

      await updatePhaseProgressFromTasks(phaseId);
    } catch (err) {
      console.error('Error deleting task:', err);
    } finally {
      setTaskEditBusyId(null);
    }
  }, [updatePhaseProgressFromTasks]);

  const handleMoveTask = useCallback(async (phaseId, taskId, direction) => {
    if (!phaseId || !taskId || !direction) return;

    const tasks = [...(phaseTasksMap?.[phaseId] || [])]?.sort(
      (a, b) => (Number(a?.task_order) || 0) - (Number(b?.task_order) || 0)
    );

    const currentIndex = tasks?.findIndex((task) => task?.id === taskId);
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= tasks?.length) return;

    [tasks[currentIndex], tasks[targetIndex]] = [tasks?.[targetIndex], tasks?.[currentIndex]];

    const reordered = tasks?.map((task, index) => ({
      ...task,
      task_order: index,
    }));

    setTaskEditBusyId(`${phaseId}:reorder`);

    try {
      await Promise.all(
        reordered?.map((task) =>
          supabase
            ?.from('phase_tasks')
            ?.update({
              task_order: task?.task_order,
              updated_at: new Date()?.toISOString(),
            })
            ?.eq('id', task?.id)
        )
      );

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: reordered,
      }));
    } catch (err) {
      console.error('Error reordering task:', err);
    } finally {
      setTaskEditBusyId(null);
    }
  }, [phaseTasksMap]);

  const handleAddCustomPhase = useCallback(async (phaseName) => {
    const cleanName = phaseName?.trim();
    if (!project?.id || !cleanName) return null;

    setAddingCustomPhase(true);

    try {
      const now = new Date()?.toISOString();

      const existingOrders = (phases || [])
        ?.map((phase) => Number(phase?.phase_order) || 0)
        ?.filter((order) => order > 0);

      const nextOrder = existingOrders?.length ? Math.max(...existingOrders) + 1 : 1;

      const { data: insertedPhase, error } = await supabase
        ?.from('phases')
        ?.insert({
          project_id: project?.id,
          phase_name: cleanName,
          source_task_name: cleanName,
          phase_order: nextOrder,
          planned_start_date: null,
          planned_end_date: null,
          actual_start_date: null,
          actual_end_date: null,
          status: 'pending',
          progress: 0,
          assigned_worker_id: null,
          is_delayed: false,
          is_urgent: false,
          required_trade: null,
          plumber_progress: 0,
          electrician_progress: 0,
          aircon_progress: 0,
          storey: null,
          details_json: [],
          total_cost: null,
          owner_type: 'unselected',
          created_at: now,
          updated_at: now,
        })
        ?.select('*')
        ?.single();

      if (error) {
        console.error('Error adding custom phase:', error);
        alert(`Failed to add custom phase: ${error?.message || 'Unknown error'}`);
        return null;
      }

      const nextPhases = [...(phases || []), insertedPhase]?.sort(
        (a, b) => (Number(a?.phase_order) || 0) - (Number(b?.phase_order) || 0)
      );

      setReorderedPhases(nextPhases);
      setPhases(nextPhases);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [insertedPhase?.id]: [],
      }));

      return insertedPhase;
    } catch (err) {
      console.error('Unexpected error adding custom phase:', err);
      alert('Unexpected error while adding custom phase. Check the browser console for details.');
      return null;
    } finally {
      setAddingCustomPhase(false);
    }
  }, [project?.id, phases]);

  const handleRenamePhase = useCallback(async (phaseId, nextName) => {
    if (!phaseId || !nextName?.trim()) return;

    setPhaseEditBusyId(phaseId);

    try {
      const cleanName = nextName?.trim();
      const now = new Date()?.toISOString();

      const { error } = await supabase
        ?.from('phases')
        ?.update({
          phase_name: cleanName,
          updated_at: now,
        })
        ?.eq('id', phaseId);

      if (error) throw error;

      setPhases((prev) =>
        prev?.map((phase) =>
          phase?.id === phaseId ? { ...phase, phase_name: cleanName, updated_at: now } : phase
        )
      );

      setReorderedPhases((prev) =>
        prev
          ? prev?.map((phase) =>
              phase?.id === phaseId ? { ...phase, phase_name: cleanName, updated_at: now } : phase
            )
          : prev
      );
    } catch (err) {
      console.error('Error renaming phase:', err);
    } finally {
      setPhaseEditBusyId(null);
    }
  }, []);

  const handleDeletePhase = useCallback(async (phase) => {
    if (!phase?.id) return;

    const confirmed = window.confirm(
      `Delete phase "${phase?.phase_name || phase?.source_task_name || 'this phase'}"? This will also delete all tasks inside this phase.`
    );

    if (!confirmed) return;

    setPhaseEditBusyId(phase?.id);

    try {
      const projectId = phase?.project_id || project?.id;

      await supabase?.from('phase_tasks')?.delete()?.eq('phase_id', phase?.id);

      const { error } = await supabase?.from('phases')?.delete()?.eq('id', phase?.id);
      if (error) throw error;

      const { data: freshRemaining, error: fetchError } = await supabase
        ?.from('phases')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('phase_order', { ascending: true });

      if (fetchError) throw fetchError;

      const reorderedRemaining = (freshRemaining || [])?.map((p, index) => ({
        ...p,
        phase_order: index + 1,
      }));

      setPhaseTasksMap((prev) => {
        const next = { ...prev };
        delete next?.[phase?.id];
        return next;
      });

      setReorderedPhases(null);
      setPhases(reorderedRemaining);
    } catch (err) {
      console.error('Unexpected phase delete error:', err);
      alert('Unexpected error while deleting phase. Check the browser console for details.');
    } finally {
      setPhaseEditBusyId(null);
    }
  }, [project?.id]);

  const handleAssignWorkerForPhase = useCallback(async (phaseId, workerId) => {
    if (!phaseId) return;

    setAssigningPhaseId(phaseId);

    try {
      const { error } = await supabase
        ?.from('phase_tasks')
        ?.update({
          assigned_worker_id: workerId || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('phase_id', phaseId);

      if (error) throw error;

      const freshTasks = await refetchPhaseTasksForPhase(phaseId);

      setPhaseTasksMap((prev) => ({
        ...prev,
        [phaseId]: freshTasks || [],
      }));
    } catch (err) {
      console.error('Error assigning phase worker:', err);
    } finally {
      setAssigningPhaseId(null);
    }
  }, []);

  const handleManualPhaseDateChange = useCallback(async (phaseId, field, value) => {
    if (!phaseId || !field) return;

    setSavingPhaseDateId(phaseId);

    try {
      const { error } = await supabase
        ?.from('phases')
        ?.update({
          [field]: value || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', phaseId);

      if (error) throw error;

      setPhases((prev) =>
        prev?.map((phase) =>
          phase?.id === phaseId
            ? {
                ...phase,
                [field]: value || null,
                ...(field === 'planned_start_date' ? { estimated_start_date: value || null } : {}),
                ...(field === 'planned_end_date' ? { estimated_end_date: value || null } : {}),
              }
            : phase
        )
      );

      setReorderedPhases((prev) =>
        prev
          ? prev?.map((phase) =>
              phase?.id === phaseId
                ? {
                    ...phase,
                    [field]: value || null,
                    ...(field === 'planned_start_date' ? { estimated_start_date: value || null } : {}),
                    ...(field === 'planned_end_date' ? { estimated_end_date: value || null } : {}),
                  }
                : phase
            )
          : prev
      );
    } catch (err) {
      console.error('Error manually changing phase dates:', err);
    } finally {
      setSavingPhaseDateId(null);
    }
  }, []);

  const handleApplySequencing = useCallback(async () => {
    if (!projectStartDate) return;

    setSequencing(true);
    setSequencingError('');

    try {
      const phasesToSequence = reorderedPhases || sortedPhases;
      const sequenced = sequencePhases(projectStartDate, phasesToSequence);

      if (!sequenced?.length) {
        setSequencingError('No phases to sequence.');
        return;
      }

      await Promise.all(
        sequenced?.map((phase) =>
          supabase
            ?.from('phases')
            ?.update({
              planned_start_date: phase?.estimated_start_date,
              planned_end_date: phase?.estimated_end_date,
              phase_order: phase?.phase_order,
              updated_at: new Date()?.toISOString(),
            })
            ?.eq('id', phase?.id)
        )
      );

      const maxEndDate = sequenced
        ?.map((phase) => phase?.estimated_end_date)
        ?.filter(Boolean)
        ?.sort()
        ?.slice(-1)?.[0];

      await supabase
        ?.from('projects')
        ?.update({
          start_date: toISODate(projectStartDate),
          end_date: maxEndDate || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', project?.id);

      const updatedPhases = sequenced?.map((phase) => ({
        ...phase,
        planned_start_date: phase?.estimated_start_date,
        planned_end_date: phase?.estimated_end_date,
        estimated_start_date: phase?.estimated_start_date,
        estimated_end_date: phase?.estimated_end_date,
        durationDays: phase?.durationDays,
      }));

      setPhases((prev) =>
        prev?.map((phase) => {
          const seq = updatedPhases?.find((s) => s?.id === phase?.id);
          return seq ? { ...phase, ...seq } : phase;
        })
      );

      setReorderedPhases(updatedPhases);
      setSequencingDone(true);
    } catch (err) {
      console.error('Error applying sequencing:', err);
      setSequencingError(err?.message || 'Failed to apply sequencing. Please try again.');
    } finally {
      setSequencing(false);
    }
  }, [projectStartDate, sortedPhases, reorderedPhases, project?.id]);

  const handleApproveProject = useCallback(async () => {
    if (!project?.id) return;

    setApproving(true);
    setApproveError('');

    try {
      await projectService?.approveDraftProject(project?.id);
      navigate('/boss-home-page');
    } catch (err) {
      console.error('Error approving draft project:', err);
      setApproveError(err?.message || 'Failed to approve project');
    } finally {
      setApproving(false);
    }
  }, [project?.id, navigate]);

  const handleStartProject = useCallback(async () => {
    if (!project?.id || !sortedPhases?.length) return;

    setStartingProject(true);
    setStartError('');

    try {
      const now = new Date()?.toISOString();
      const today = now?.split('T')?.[0];
      const firstPhase = sortedPhases?.[0];

      const { error: projectError } = await supabase
        ?.from('projects')
        ?.update({
          status: 'on-track',
          lifecycle_status: 'active',
          start_date: today,
          updated_at: now,
        })
        ?.eq('id', project?.id);

      if (projectError) throw projectError;

      const { error: phaseError } = await supabase
        ?.from('phases')
        ?.update({
          status: 'ongoing',
          actual_start_date: today,
          updated_at: now,
        })
        ?.eq('id', firstPhase?.id);

      if (phaseError) throw phaseError;

      setPhases((prev) =>
        prev?.map((phase) =>
          phase?.id === firstPhase?.id ? { ...phase, status: 'ongoing', actual_start_date: today } : phase
        )
      );

      onProjectStart?.({
        lifecycle_status: 'active',
        start_date: today,
      });
    } catch (err) {
      console.error('Error starting project:', err);
      setStartError('An unexpected error occurred. Please try again.');
    } finally {
      setStartingProject(false);
    }
  }, [project?.id, sortedPhases, onProjectStart]);

  const handleNextPhase = useCallback(async (currentPhaseId) => {
    if (!currentPhaseId) return;

    setAdvancingPhaseId(currentPhaseId);

    try {
      const now = new Date()?.toISOString();
      const today = now?.split('T')?.[0];
      const currentIndex = sortedPhases?.findIndex((p) => p?.id === currentPhaseId);
      const nextPhase = sortedPhases?.[currentIndex + 1];

      await supabase
        ?.from('phases')
        ?.update({
          status: 'completed',
          actual_end_date: today,
          progress: 100,
          updated_at: now,
        })
        ?.eq('id', currentPhaseId);

      if (nextPhase) {
        await supabase
          ?.from('phases')
          ?.update({
            status: 'ongoing',
            actual_start_date: today,
            updated_at: now,
          })
          ?.eq('id', nextPhase?.id);
      }

      setPhases((prev) =>
        prev?.map((phase) => {
          if (phase?.id === currentPhaseId) {
            return { ...phase, status: 'completed', actual_end_date: today, progress: 100 };
          }
          if (nextPhase && phase?.id === nextPhase?.id) {
            return { ...phase, status: 'ongoing', actual_start_date: today };
          }
          return phase;
        })
      );
    } catch (err) {
      console.error('Error advancing phase:', err);
    } finally {
      setAdvancingPhaseId(null);
    }
  }, [sortedPhases]);

  const handleCompleteProject = useCallback(async (currentPhaseId) => {
    if (!currentPhaseId) return;

    setAdvancingPhaseId(currentPhaseId);

    try {
      const now = new Date()?.toISOString();
      const today = now?.split('T')?.[0];

      await supabase
        ?.from('phases')
        ?.update({
          status: 'completed',
          actual_end_date: today,
          progress: 100,
          updated_at: now,
        })
        ?.eq('id', currentPhaseId);

      await supabase
        ?.from('projects')
        ?.update({
          status: 'completed',
          lifecycle_status: 'completed',
          updated_at: now,
        })
        ?.eq('id', project?.id);

      setPhases((prev) =>
        prev?.map((phase) =>
          phase?.id === currentPhaseId ? { ...phase, status: 'completed', actual_end_date: today, progress: 100 } : phase
        )
      );
    } catch (err) {
      console.error('Error completing project:', err);
    } finally {
      setAdvancingPhaseId(null);
    }
  }, [project?.id]);

  const handleSaveProjectName = useCallback(async () => {
    const nextName = projectNameText?.trim();
    if (!project?.id || !nextName) return;

    setSavingProjectName(true);

    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.update({
          name: nextName,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', project?.id)
        ?.select('id, name')
        ?.single();

      if (error) throw error;

      setDisplayProjectName(data?.name || nextName);
      setProjectNameText(data?.name || nextName);
      setEditingProjectName(false);
    } catch (err) {
      console.error('Unexpected error updating project name:', err);
      alert('Unexpected error while updating project name.');
    } finally {
      setSavingProjectName(false);
    }
  }, [project?.id, projectNameText]);

  const overallProgress = useMemo(() => {
    if (!sortedPhases?.length) return 0;

    const phaseProgressValues = sortedPhases?.map((phase) => {
      if (phase?.status === 'completed') {
        return Number(phase?.progress ?? 100) || 100;
      }

      const tasks = phaseTasksMap?.[phase?.id] || [];
      if (tasks?.length > 0) {
        const completed = tasks?.filter((task) => task?.is_completed)?.length || 0;
        return Math.round((completed / tasks?.length) * 100);
      }

      return Number(phase?.progress) || 0;
    });

    if (!phaseProgressValues?.length) return 0;

    return Math.round(
      phaseProgressValues?.reduce((sum, value) => sum + value, 0) / phaseProgressValues?.length
    );
  }, [sortedPhases, phaseTasksMap]);

  const visiblePhases = useMemo(() => {
    if (!isWorkerView) return sortedPhases;
    return sortedPhases?.filter((phase) => (phaseTasksMap?.[phase?.id] || [])?.length > 0);
  }, [sortedPhases, phaseTasksMap, isWorkerView]);

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <button
                onClick={onBack}
                className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"
                title="Back"
              >
                <Icon name="ArrowLeft" size={18} />
              </button>

              <div className="flex items-center gap-1 min-w-0" onClick={(e) => e?.stopPropagation()}>
                {editingProjectName && !isWorkerView ? (
                  <div className="flex items-center gap-1 min-w-[260px] max-w-xl">
                    <input
                      value={projectNameText}
                      onChange={(e) => setProjectNameText(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter') handleSaveProjectName();
                        if (e?.key === 'Escape') setEditingProjectName(false);
                      }}
                      className="w-full rounded-md border border-border bg-background px-2 py-1 text-xl font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleSaveProjectName}
                      disabled={savingProjectName || !projectNameText?.trim()}
                      className="p-1.5 rounded-md text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Save size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingProjectName(false)}
                      disabled={savingProjectName}
                      className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (isWorkerView) return;
                      setProjectNameText(displayProjectName || projectName || project?.name || '');
                      setEditingProjectName(true);
                    }}
                    disabled={isWorkerView || savingProjectName}
                    className="group flex items-center gap-1 min-w-0 text-left disabled:cursor-default"
                  >
                    <h1 className="text-xl font-bold text-foreground truncate group-hover:text-primary transition-colors">
                      {displayProjectName || projectName || project?.name}
                    </h1>
                    {!isWorkerView && (
                      <Pencil size={14} className="text-muted-foreground group-hover:text-primary flex-shrink-0" />
                    )}
                  </button>
                )}
              </div>

              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 text-xs font-medium">
                <Sparkles size={11} />
                AI Imported
              </span>

              {isDraft && !isWorkerView && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium">
                  <Clock size={11} />
                  Draft
                </span>
              )}
            </div>

            <p className="text-sm text-muted-foreground ml-9">
              {visiblePhases?.length} phase{visiblePhases?.length !== 1 ? 's' : ''} · AI-generated from quotation PDF
            </p>

            {estimatedDurationDays != null && (
              <div className="ml-9 mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock size={14} className="text-primary" />
                <span>
                  Estimated duration:{' '}
                  <span className="font-semibold text-foreground">
                    {formatDuration(estimatedDurationDays)}
                  </span>
                </span>
              </div>
            )}

            {estimatedEndDate && (
              <div className="ml-9 mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar size={14} className="text-primary" />
                <span>
                  Estimated completion:{' '}
                  <span className="font-semibold text-foreground">
                    {formatDisplayDate(estimatedEndDate)}
                  </span>
                </span>
              </div>
            )}

            {!isDraft &&
              !isWorkerView &&
              overallProgress === 0 &&
              !sortedPhases?.some((p) => p?.status === 'ongoing' || p?.status === 'completed') && (
                <div className="ml-9 mt-3">
                  <button
                    onClick={handleStartProject}
                    disabled={startingProject}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {startingProject ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Starting…
                      </>
                    ) : (
                      <>
                        <Play size={14} />
                        Start Project
                      </>
                    )}
                  </button>

                  {startError && (
                    <p className="mt-2 text-xs text-destructive flex items-center gap-1">
                      <AlertCircle size={12} />
                      {startError}
                    </p>
                  )}
                </div>
              )}

            {approveError && !isWorkerView && (
              <div className="ml-9 mt-3 flex items-center gap-2 text-sm text-destructive">
                <AlertCircle size={15} />
                {approveError}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            {isDraft && !isWorkerView && (
              <button
                onClick={handleApproveProject}
                disabled={approving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {approving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" />
                    Approving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={15} />
                    Approve Project
                  </>
                )}
              </button>
            )}

            <div className="flex flex-col items-end gap-1">
              <span className="text-2xl font-bold text-primary">{overallProgress}%</span>
              <span className="text-xs text-muted-foreground">Overall</span>
              <div className="w-28 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isWorkerView && (
        <div className="bg-card border border-amber-200 rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between p-5 hover:bg-muted/20 transition-colors"
            onClick={() => setShowSequencingPanel((p) => !p)}
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-amber-600" />
              </div>

              <div className="text-left">
                <h2 className="text-sm font-semibold text-foreground">
                  Project Schedule & Phase Sequencing
                </h2>

                <p className="text-xs text-muted-foreground mt-0.5">
                  {projectStartDate
                    ? `Start date: ${formatDisplayDate(projectStartDate)} · ${sortedPhases?.length} phases sequenced`
                    : 'Set a start date to schedule phases automatically'}
                </p>
              </div>
            </div>

            {showSequencingPanel ? (
              <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
            )}
          </button>

          {showSequencingPanel && (
            <div className="px-5 pb-5 border-t border-amber-100">
              <div className="flex items-end gap-3 flex-wrap mt-4">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-foreground mb-1">
                    Project Start Date
                  </label>

                  <input
                    type="date"
                    value={projectStartDate}
                    onChange={(e) => {
                      setProjectStartDate(e?.target?.value);
                      setSequencingDone(false);
                      setSequencingError('');
                    }}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>

                <button
                  onClick={handleApplySequencing}
                  disabled={!projectStartDate || sequencing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sequencing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" />
                      Calculating…
                    </>
                  ) : (
                    <>
                      <Calendar size={15} />
                      Apply Schedule
                    </>
                  )}
                </button>
              </div>

              {sequencingDone && !sequencingError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle2 size={15} />
                  Phase dates calculated and saved. Review the estimated schedule below.
                </div>
              )}

              {sequencingError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle size={15} />
                  {sequencingError}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border">
                <button
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                  onClick={() => setShowSequenceOverride((p) => !p)}
                >
                  {showSequenceOverride ? (
                    <ChevronDown size={15} />
                  ) : (
                    <ChevronRight size={15} />
                  )}
                  Phase Sequence Override
                  <span className="text-xs text-muted-foreground font-normal ml-1">
                    ({sortedPhases?.length} phases · reorder if needed)
                  </span>
                </button>

                {showSequenceOverride && (
                  <div className="mt-3">
                    <PhaseSequencingOverride
                      phases={sortedPhases}
                      projectStartDate={projectStartDate}
                      onReorder={(reordered) => {
                        setReorderedPhases(reordered);
                        setSequencingDone(false);
                      }}
                      onManualDateChange={handleManualPhaseDateChange}
                      savingPhaseDateId={savingPhaseDateId}
                      onAddPhase={handleAddCustomPhase}
                      addingCustomPhase={addingCustomPhase}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {seeding && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-1">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          Preparing task checklist…
        </div>
      )}

      {visiblePhases?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            {isWorkerView
              ? 'No tasks have been assigned to you for this AI project yet.'
              : 'No phases found for this project.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visiblePhases?.map((phase, index) => (
            <PhaseCard
              key={phase?.id}
              phase={phase}
              phaseTasks={phaseTasksMap?.[phase?.id] || []}
              onToggle={handleToggle}
              onAssignWorkerForPhase={handleAssignWorkerForPhase}
              workers={workers}
              assigningPhaseId={assigningPhaseId}
              isDraft={isDraft}
              phaseOrder={index + 1}
              isWorkerView={isWorkerView}
              isOngoing={phase?.status === 'ongoing'}
              onNextPhase={handleNextPhase}
              advancingPhase={advancingPhaseId === phase?.id}
              isLastPhase={index === visiblePhases?.length - 1}
              onCompleteProject={handleCompleteProject}
              onPhaseOwnerChoice={handlePhaseOwnerChoice}
              selectingOwnerPhaseId={selectingOwnerPhaseId}
              onResetPhaseOwner={handleResetPhaseOwner}
              resettingOwnerPhaseId={resettingOwnerPhaseId}
              onAddTask={handleAddTask}
              onRenameTask={handleRenameTask}
              onDeleteTask={handleDeleteTask}
              onMoveTask={handleMoveTask}
              taskEditBusyId={taskEditBusyId}
              onRenamePhase={handleRenamePhase}
              onDeletePhase={handleDeletePhase}
              phaseEditBusyId={phaseEditBusyId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AIPhaseManagement;
